// Copyright 2025, Imprivata, Inc.  All rights reserved.

import Icon from '@ant-design/icons';
import { Button, type ButtonProps } from '@imprivata-cloud/components';
import { Tooltip } from 'antd';
import { CopyCheck } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import copyIcon from '../../assets/icons/copy.svg?react';
import { copyToClipboard } from '../../utils/utils';
import './copyButton.less';

interface CopyButtonProps extends ButtonProps {
  textToCopy: string;
  dynamicLabel?: { initialLabel: string; labelAfterClick: string; dynamicLabelDuration?: number };
  externalCopied?: boolean;
  onExternalCopiedReset?: () => void;
}

const CopyButton: React.FC<CopyButtonProps> = ({
  textToCopy,
  label,
  icon = <Icon component={copyIcon} />,
  className = '',
  size = 'small',
  dynamicLabel,
  onClick,
  externalCopied,
  onExternalCopiedReset,
  ...rest
}) => {
  const { t } = useTranslation();
  const [labelText, setLabelText] = useState(label || dynamicLabel?.initialLabel);
  const [copied, setCopied] = useState(false);
  const duration = dynamicLabel?.dynamicLabelDuration ?? 2000;
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  const scheduleReset = (afterReset?: () => void) => {
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      setCopied(false);
      if (dynamicLabel) {
        setLabelText(dynamicLabel.initialLabel);
      }
      afterReset?.();
    }, duration);
  };

  // Clear any pending timer on unmount to avoid setting state after unmount.
  useEffect(() => () => clearTimeout(resetTimer.current), []);

  // Ctrl+C path: parent signals a copy happened elsewhere for this value.
  // biome-ignore lint/correctness/useExhaustiveDependencies: react only to the externalCopied signal edge
  useEffect(() => {
    if (externalCopied) {
      setCopied(true);
      if (dynamicLabel) {
        setLabelText(dynamicLabel.labelAfterClick);
      }
      scheduleReset(onExternalCopiedReset);
    }
  }, [externalCopied]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick?.(event);
    copyToClipboard(textToCopy);
    setCopied(true);
    if (dynamicLabel) {
      setLabelText(dynamicLabel.labelAfterClick);
    }
    scheduleReset();
  };

  return (
    <Tooltip
      title={copied ? t('common.copied') : t('common.copy')}
      placement="bottom"
      overlayClassName="copy-copied-tooltip"
    >
      <Button
        aria-label="copy-button"
        type="text"
        size={size}
        label={labelText}
        icon={copied ? <CopyCheck size={20} className="copy-check-icon" /> : icon}
        onClick={handleClick}
        className={`copy-button ${className}`}
        {...rest}
      />
    </Tooltip>
  );
};

export default CopyButton;
